import { api } from './client';
import type { Page } from './catalogs';

export type ReceiptOperationType = 'FROM_BUYER' | 'FROM_BANK' | 'FROM_PODOTCHET' | 'LOAN' | 'LOAN_GIVEN_RETURN' | 'TRANSIT_IN' | 'OTHER';
export type PaymentOperationType =
  | 'TO_SUPPLIER' | 'TO_BANK' | 'TO_PODOTCHET' | 'SALARY' | 'LOAN_RETURN' | 'LOAN_GIVEN' | 'TRANSIT_OUT' | 'OTHER'
  | 'DEPOSIT_PAYOUT';  // ч.770: виплата депонованої ЗП (Дт 662 — Кт 301)

// Block 5.3a (2026-05-27): CancellableMixin state.
export type CancellableState = 'DRAFT' | 'POSTED' | 'CANCELLED';

// ----- ПКО -----

export interface CashReceiptListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  cash_register_id: string;
  contractor_id: string | null;
  operation_type: string;
  amount: string;
  received_from: string | null;
  basis: string | null;
  posted: boolean;
  deletion_mark: boolean;
  project_id?: string | null;
  // Block 5.3a:
  state?: CancellableState;
}

export interface CashReceipt extends CashReceiptListItem {
  contract_id: string | null;
  customer_order_id: string | null;
  employee_id: string | null;
  appendix: string | null;
  cash_flow_item_id: string | null;
  bank_account_id?: string | null;   // ч.1349: «з банку» / «в банк»
  vat_rate_id?: string | null;       // ч.1446: ставка і сума ПДВ, як у ПКО 1С
  vat_amount?: string | number | null;
  comment: string | null;
  posted_at: string | null;
  // Block 5.3a: cancellation fields.
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface CashReceiptCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  cash_register_id: string;
  bank_account_id?: string | null;
  operation_type: ReceiptOperationType;
  contractor_id?: string | null;
  contract_id?: string | null;
  customer_order_id?: string | null;
  employee_id?: string | null;
  amount: string;
  vat_rate_id?: string | null;   // ч.1446
  vat_amount?: string;
  received_from?: string | null;
  basis?: string | null;
  comment?: string | null;
}

export type CashReceiptUpdate = Partial<CashReceiptCreate>;

export const cashReceipts = {
  list: (params?: { organization_id?: string; contractor_id?: string; posted?: boolean; limit?: number; offset?: number; date_from?: string; date_to?: string; sort_by?: string; sort_dir?: 'asc' | 'desc'; /** ч.949: фільтр за підставою. */ basis?: string }) =>
    api.get<Page<CashReceiptListItem>>('/cash-receipts', { params }).then((r) => r.data),
  get: (id: string) => api.get<CashReceipt>(`/cash-receipts/${id}`).then((r) => r.data),
  create: (payload: CashReceiptCreate) =>
    api.post<CashReceipt>('/cash-receipts', payload).then((r) => r.data),
  update: (id: string, payload: CashReceiptUpdate) =>
    api.patch<CashReceipt>(`/cash-receipts/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/cash-receipts/${id}`).then(() => undefined),
  post: (id: string) => api.post<CashReceipt>(`/cash-receipts/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<CashReceipt>(`/cash-receipts/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/cash-receipts/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};

// ----- РКО -----

export interface CashPaymentListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  cash_register_id: string;
  contractor_id: string | null;
  operation_type: string;
  amount: string;
  issued_to: string | null;
  basis: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  project_id?: string | null;
  // Block 5.3a:
  state?: CancellableState;
}

export interface CashPayment extends CashPaymentListItem {
  contract_id: string | null;
  supplier_order_id: string | null;
  employee_id: string | null;
  appendix: string | null;
  cash_flow_item_id: string | null;
  bank_account_id?: string | null;   // ч.1349: «з банку» / «в банк»
  comment: string | null;
  posted_at: string | null;
  // Block 5.3a: cancellation fields.
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface CashPaymentCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  cash_register_id: string;
  bank_account_id?: string | null;
  operation_type: PaymentOperationType;
  contractor_id?: string | null;
  contract_id?: string | null;
  supplier_order_id?: string | null;
  employee_id?: string | null;
  amount: string;
  issued_to?: string | null;
  basis?: string | null;
  comment?: string | null;
}

export type CashPaymentUpdate = Partial<CashPaymentCreate>;

export const cashPayments = {
  list: (params?: {
    organization_id?: string;
    contractor_id?: string;
    employee_id?: string;
    operation_type?: string;
    posted?: boolean;
    limit?: number;
    offset?: number;
    date_from?: string;
    date_to?: string;
    /** ч.949: фільтр за підставою (пошук по тексту). */
    basis?: string;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
  }) =>
    api.get<Page<CashPaymentListItem>>('/cash-payments', { params }).then((r) => r.data),
  get: (id: string) => api.get<CashPayment>(`/cash-payments/${id}`).then((r) => r.data),
  create: (payload: CashPaymentCreate) =>
    api.post<CashPayment>('/cash-payments', payload).then((r) => r.data),
  update: (id: string, payload: CashPaymentUpdate) =>
    api.patch<CashPayment>(`/cash-payments/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/cash-payments/${id}`).then(() => undefined),
  post: (id: string) => api.post<CashPayment>(`/cash-payments/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<CashPayment>(`/cash-payments/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/cash-payments/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
